import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Button } from '@chakra-ui/react';

import { getSessionConfig, keepaliveSession, logout } from '../api/auth';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

function SessionTimeoutMonitor({ user, dispatch }) {
    const [config, setConfig] = useState({ timeout_minutes: 60, warning_seconds: 30 });
    const [showWarning, setShowWarning] = useState(false);
    const [timerVersion, setTimerVersion] = useState(0);
    const warningTimer = useRef(null);
    const logoutTimer = useRef(null);
    const lastActivity = useRef(Date.now());
    const cancelRef = useRef();

    const clearTimers = () => {
        if (warningTimer.current) clearTimeout(warningTimer.current);
        if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };

    const endSession = useCallback(() => {
        logout().catch(() => {}).finally(() => {
            dispatch({ type: 'LOGOUT' });
            window.location = '/login';
        });
    }, [dispatch]);

    useEffect(() => {
        if (!user) return undefined;
        getSessionConfig().then(res => {
            setConfig(res.data.data);
        }).catch(() => {});
        return undefined;
    }, [user]);

    useEffect(() => {
        if (!user) return undefined;
        const scheduleTimers = () => {
            clearTimers();
            const timeoutMs = Number(config.timeout_minutes || 60) * 60 * 1000;
            const warningMs = Number(config.warning_seconds || 30) * 1000;
            const elapsed = Date.now() - lastActivity.current;
            const remaining = Math.max(timeoutMs - elapsed, 0);
            warningTimer.current = setTimeout(() => setShowWarning(true), Math.max(remaining - warningMs, 0));
            logoutTimer.current = setTimeout(endSession, remaining);
        };
        const onActivity = () => {
            if (showWarning) return;
            lastActivity.current = Date.now();
            scheduleTimers();
        };
        ACTIVITY_EVENTS.forEach(eventName => window.addEventListener(eventName, onActivity, { passive: true }));
        scheduleTimers();
        return () => {
            ACTIVITY_EVENTS.forEach(eventName => window.removeEventListener(eventName, onActivity));
            clearTimers();
        };
    }, [user, config.timeout_minutes, config.warning_seconds, showWarning, timerVersion, endSession]);

    const continueSession = () => {
        clearTimers();
        keepaliveSession().then(() => {
            lastActivity.current = Date.now();
            setShowWarning(false);
            setTimerVersion(version => version + 1);
        }).catch(endSession);
    };

    if (!user) return null;

    return (
        <AlertDialog isOpen={showWarning} leastDestructiveRef={cancelRef} onClose={continueSession} isCentered>
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">Sesión por vencer</AlertDialogHeader>
                    <AlertDialogBody>
                        Tu sesión se cerrará por inactividad. ¿Querés continuar?
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        {/* leastDestructiveRef (arriba) marca el boton que Chakra
                            ENFOCA al abrir, y estaba puesto en "Cerrar sesion":
                            un Enter o un espacio mientras se escribia cerraba la
                            sesion. Ahora apunta a "Continuar", que es lo que el
                            nombre del atributo siempre pidio. */}
                        <Button onClick={endSession} variant="outline">Cerrar sesión</Button>
                        <Button ref={cancelRef} colorScheme="purple" onClick={continueSession} ml={3}>Continuar</Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
}

export default SessionTimeoutMonitor;
